import { useMutation, useQuery } from '@tanstack/react-query';
import type { UseQueryReturnType } from '@tyro/api';
import {
  type ProgrammeStageEnrollmentFilter,
  type UpdateProgrammeStageEnrollmentInput,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { groupsKeys } from './keys';

const programmeStages = graphql(/* GraphQL */ `
query programmeStages($filter: ProgrammeStageEnrollmentFilter) {
  core_programmeStageEnrollments(filter: $filter) {
    avatarUrl
    name
    partyId
    programmeStageEnrollmentPartyId
    programmeStageId
    programmeStageLeads {
      type
      partyId
      avatarUrl
      firstName
      lastName
    }
    programmeStage {
      yearGroupId
      name
      description
      shortName
    }
    studentMembers {
      memberCount
    }
    students {
      partyId
      person {
        partyId
        title {
          id
          name
          nameTextId
        }
        firstName
        lastName
        avatarUrl
        type
      }
      classGroup {
        partyId
        name
      }
      tutors {
        title {
          id
          name
          nameTextId
        }
        firstName
        lastName
        avatarUrl
        type
      }
      extensions {
        priority
        aen
        medical
      }
      yearGroupLeads {
        avatarUrl
        type
        partyId
        firstName
        lastName
      }
      programmeStages {
        id
        programmeId
        name
      }
    }
  }
}
`);

const updateProgrammeStageEnrollmentsLeads = graphql(/* GraphQL */ `
mutation core_updateProgrammeStageEnrollments($input: [UpdateProgrammeStageEnrollmentInput]) {
  core_updateProgrammeStageEnrollments(input: $input) {
    success
  }
}
`);

const programmeStageQuery = (filter: ProgrammeStageEnrollmentFilter) => ({
  queryKey: groupsKeys.programmeStages.programmeStageEnrollments(filter),
  queryFn: () => gqlClient.request(programmeStages, { filter }),
});

export function getProgrammeStages(filter: ProgrammeStageEnrollmentFilter) {
  return queryClient.fetchQuery(programmeStageQuery(filter));
}

export function useProgrammeStages(filter: ProgrammeStageEnrollmentFilter) {
  return useQuery({
    ...programmeStageQuery(filter),
    select: ({ core_programmeStageEnrollments }) => {
      if (!Array.isArray(core_programmeStageEnrollments)) return [];

      return core_programmeStageEnrollments.sort((a, b) =>
        a.name.localeCompare(b.name),
      );
    },
  });
}

export function useUpdateProgrammeStageEnrollmentLeads() {
  return useMutation({
    mutationFn: (input: UpdateProgrammeStageEnrollmentInput[]) =>
      gqlClient.request(updateProgrammeStageEnrollmentsLeads, { input }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: groupsKeys.programmeStages.all(),
      });
    },
  });
}

export type ReturnTypeProgrammeStage = UseQueryReturnType<
  typeof useProgrammeStages
>[number];
